import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { SisterProductLinks } from "@/components/site/SisterProductLinks";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { stateRateSectionCopy } from "@/lib/dscr-rate-copy";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { stateKeywordsMeta } from "@/lib/page-keywords";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { getStateHero } from "@/data/state-heroes";
import { getState } from "@/data/states";
import { getMarketStats } from "@/lib/market-stats.functions";
import { getHousingMomentum } from "@/lib/housing-momentum.functions";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { StateProductPage } from "@/components/site/StateProductPage";
import { stateProductFaqJsonLd, stateProductServiceJsonLd, stateProductMetaDescription, buildStateFaq } from "@/components/site/StateProductPage.seo";
import { productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { buildHead } from "@/lib/seo-head";
import { stateScenarioLocation } from "@/lib/state-location";

const SLUG = "dscr-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;

export const Route = createFileRoute("/dscr-loans/$state/")({
  loader: async ({ params }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    const [liveStats, housingMomentum, countyInsights] = await Promise.all([
      getMarketStats({ data: { stateSlug: params.state } }).catch(() => null),
      getHousingMomentum({ data: { stateAbbrev: state.abbrev, stateName: state.name } }).catch(() => null),
      getCountyInsights({ data: { stateAbbrev: state.abbrev, placeLabel: state.name } }).catch(() => null),
    ]);
    return { state, liveStats, housingMomentum, countyInsights };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: `${PRODUCT.shortName} loans | Simply Approved Mortgages` }, { name: "robots", content: "noindex" }] };
    const url = `/${SLUG}/${params.state}`;
    const stateHero = getStateHero(params.state) ?? HERO;
    const title = `${PRODUCT.shortName} in ${state.name} | Simply Approved`;
    const description = stateProductMetaDescription(PRODUCT, state);
    return buildHead({
      title,
      description,
      url,
      keywords: stateKeywordsMeta(PRODUCT.slug, state.name),
      image: stateHero.src,
      imageAlt: stateHero.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: PRODUCT.shortName, url: `/${SLUG}` },
        { name: state.name, url },
      ],
      faqs: buildStateFaq(PRODUCT, state),
      service: { name: `${PRODUCT.shortName} Loans`, state: state.name },
      extraJsonLd: [
        stateProductServiceJsonLd(PRODUCT, state, url),
        productHowToJsonLd(PRODUCT, url),
        productArticleJsonLd(PRODUCT, url),
      ],
    });
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { state, liveStats, housingMomentum, countyInsights } = Route.useLoaderData();
  return (
    <StateProductPage
      product={PRODUCT}
      state={state}
      liveStats={liveStats}
      housingMomentum={housingMomentum}
      countyInsights={countyInsights}
      topSlot={
        <LiveDscrRateSection
          strategyCards
          bare
          {...stateRateSectionCopy(state)}
          scenarioOverrides={{ ...stateScenarioLocation(state.abbrev) }}
          toolLabel={`${state.name} DSCR`}
        />
      }
      midSlot={
        <div className="space-y-8">
          <ProductReportCtaBanner productKey="dscr" variant="full" locationLabel={state.name} />
          <SisterProductLinks self="dscr" stateSlug={state.slug} stateName={state.name} />
          <DscrProgramLinks
            eyebrow="DSCR structures"
            title={`Other DSCR structures available in ${state.name}`}
            intro={`Cash-out, rate and term, HELOC, no-ratio, and interest-only DSCR financing are all available on ${state.name} rental property under the same guidelines.`}
          />
        </div>
      }
    />

  );
}
